import { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  type Table as TanStackTable,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Spinner } from "@/components/ui/spinner";

type EntityId = number | string;

interface TraderRepresentativePerson {
  contact_email?: string | null;
  contact_phone?: string | null;
  first_name?: string | null;
  full_name?: string | null;
  last_name?: string | null;
  [key: string]: unknown;
}

interface TraderRepresentativeLocation {
  name?: string | null;
  [key: string]: unknown;
}

interface TraderRepresentativeOrganization {
  id?: EntityId | null;
  name?: string | null;
  trader_type?: string | null;
  physical_address?: string | null;
  location_details?: TraderRepresentativeLocation | null;
  [key: string]: unknown;
}

export interface TraderRepresentativeItem {
  id?: EntityId | null;
  organization?: TraderRepresentativeOrganization | null;
  person?: TraderRepresentativePerson | null;
  /** Earliest recorded login, formatted "YYYY-MM-DD HH:mm:ss" by the backend serializer. */
  first_login?: string | null;
  /** Most recent recorded login, formatted "YYYY-MM-DD HH:mm:ss" by the backend serializer. */
  last_login?: string | null;
  [key: string]: unknown;
}

interface RepresentativesTableProps {
  isFetchingRepresentatives?: boolean;
  representatives?: TraderRepresentativeItem[];
}

interface RepresentativeColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type RepresentativeColumnDef = ColumnDef<TraderRepresentativeItem, unknown> & {
  meta?: RepresentativeColumnMeta;
};

const EMPTY_VALUE = "-";

const getColumnMeta = (columnDef: ColumnDef<TraderRepresentativeItem, unknown>) =>
  columnDef.meta as RepresentativeColumnMeta | undefined;

const getRepresentativeFullName = (representative: TraderRepresentativeItem) => {
  const person = representative.person;
  const fullName = person?.full_name?.trim();

  if (fullName) {
    return fullName;
  }

  return [person?.first_name, person?.last_name]
    .filter((name): name is string => Boolean(name?.trim()))
    .join(" ")
    .trim();
};

const getRepresentativePhone = (representative: TraderRepresentativeItem) =>
  representative.person?.contact_phone?.trim() || "";

const getRepresentativeEmail = (representative: TraderRepresentativeItem) =>
  representative.person?.contact_email?.trim() || "";

const formatValue = (value: string) => value || EMPTY_VALUE;

const getRepresentativeRowId = (representative: TraderRepresentativeItem, index: number) => {
  if (representative.id !== undefined && representative.id !== null) {
    return String(representative.id);
  }

  return (
    getRepresentativeEmail(representative) ||
    getRepresentativePhone(representative) ||
    `${getRepresentativeFullName(representative)}-${index}`
  );
};

const createColumns = (t: TFunction): RepresentativeColumnDef[] => [
  {
    id: "name",
    accessorFn: getRepresentativeFullName,
    header: () => t("column.name"),
    cell: ({ row }) => (
      <span className="font-medium text-foreground">
        {formatValue(getRepresentativeFullName(row.original))}
      </span>
    ),
    meta: {
      cellClassName: "min-w-56 whitespace-normal leading-5",
      headerClassName: "min-w-56",
    },
  },
  {
    id: "mobile",
    accessorFn: getRepresentativePhone,
    header: () => t("column.mobile"),
    cell: ({ row }) => formatValue(getRepresentativePhone(row.original)),
    meta: {
      cellClassName: "min-w-36",
      headerClassName: "min-w-36",
    },
  },
  {
    id: "email",
    accessorFn: getRepresentativeEmail,
    header: () => t("column.email"),
    cell: ({ row }) => formatValue(getRepresentativeEmail(row.original)),
    meta: {
      cellClassName: "min-w-72 whitespace-normal break-words leading-5 text-muted-foreground",
      headerClassName: "min-w-72",
    },
  },
];

const RepresentativesDataTable = ({
  emptyMessage,
  isLoading,
  table,
}: {
  emptyMessage: string;
  isLoading: boolean;
  table: TanStackTable<TraderRepresentativeItem>;
}) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                className={getColumnMeta(header.column.columnDef)?.headerClassName}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-20">
              <div className="flex items-center justify-center">
                <Spinner className="size-5 text-muted-foreground" />
              </div>
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length
          ? rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
  );
};

export const RepresentativesTable = ({
  isFetchingRepresentatives = false,
  representatives = [],
}: RepresentativesTableProps) => {
  const { t } = useTranslation();
  const columns = useMemo(() => createColumns(t), [t]);
  const table = useReactTable({
    columns,
    data: representatives,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRepresentativeRowId,
  });

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <RepresentativesDataTable
        emptyMessage={t("traderDetails.form.noTraderRepresentatives", {
          defaultValue: "No trader representatives found",
        })}
        isLoading={isFetchingRepresentatives}
        table={table}
      />
    </div>
  );
};

export type { RepresentativesTableProps };

export default RepresentativesTable;
